import {
  type TitleScreenHandle,
  createButton,
  createModal,
  createSettingsPanel,
  createTitleScreen,
  presetRows,
} from '@fg/ui-kit';
import Phaser from 'phaser';

export class TitleScene extends Phaser.Scene {
  private title: TitleScreenHandle | null = null;

  constructor() {
    super('title');
  }

  create(): void {
    this.title = createTitleScreen({
      title: 'Fruit Game',
      tagline: 'Drop, merge, win!',
      logo: this.buildFruitLogo(),
      onPlay: () => {
        this.title?.hide();
        this.scene.start('game');
      },
      onHowToPlay: () => this.openHowTo(),
      actions: [{ icon: '⚙', ariaLabel: 'Settings', onClick: () => this.openSettings() }],
    });
    document.body.appendChild(this.title.el);
    // Match the game's pastel-peach background; restyle text/buttons accordingly.
    this.title.el.style.background =
      'linear-gradient(165deg, #ffe3b3 0%, #ffd0a3 70%, #ffba8a 100%)';
    this.title.el.style.color = '#5a2a1f';
    const heading = this.title.el.querySelector('.fg-title__heading') as HTMLElement | null;
    if (heading) heading.style.textShadow = '0 2px 0 rgba(255,255,255,0.5)';
    this.title.show();

    // Discreet legal footer — required for Google Play / EU regulators / ad
    // network compliance. Low-contrast so it doesn't fight with PLAY.
    // import.meta.env.BASE_URL is '/' for fruit, '/fishmerge/' for fish — robust
    // to whether the visitor's URL has a trailing slash.
    const baseUrl = import.meta.env.BASE_URL;
    const footer = document.createElement('div');
    footer.style.cssText =
      'position:absolute;left:0;right:0;bottom:18px;text-align:center;font:600 11px system-ui;letter-spacing:1.2px;text-transform:uppercase;color:rgba(90,42,31,0.6);pointer-events:none;';
    const mkLink = (label: string, href: string) => {
      const a = document.createElement('a');
      a.href = href;
      a.textContent = label;
      a.style.cssText = 'color:inherit;text-decoration:none;pointer-events:auto;padding:4px 8px;';
      return a;
    };
    footer.appendChild(mkLink('Privacy', `${baseUrl}privacy`));
    const sep = document.createElement('span');
    sep.textContent = '·';
    sep.style.opacity = '0.5';
    footer.appendChild(sep);
    footer.appendChild(mkLink('Terms', `${baseUrl}terms`));
    this.title.el.appendChild(footer);

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.title?.destroy();
      this.title = null;
    });
  }

  private buildFruitLogo(): HTMLElement {
    // Decorative cluster of fruit images. The PNG strips are 1536×1024 with
    // 4 horizontal frames (384×1024 each), so each visible cell preserves its
    // tall ~0.375 aspect ratio.
    const wrap = document.createElement('div');
    wrap.style.cssText =
      'position:relative;width:240px;height:200px;display:flex;align-items:center;justify-content:center;';

    const FRAME_ASPECT = 1024 / 384; // ≈ 2.67 — frame height per frame width
    const fruits: Array<{ name: string; w: number; left: number; top: number; rot: number }> = [
      { name: 'watermelon', w: 86, left: 78, top: -6, rot: -8 },
      { name: 'apple', w: 64, left: 4, top: 26, rot: 12 },
      { name: 'orange', w: 60, left: 168, top: 36, rot: -6 },
      { name: 'cherry', w: 50, left: 92, top: 90, rot: 8 },
    ];
    for (const f of fruits) {
      const h = f.w * FRAME_ASPECT;
      const el = document.createElement('div');
      el.style.cssText = `
        position:absolute; left:${f.left}px; top:${f.top}px;
        width:${f.w}px; height:${h}px;
        background-image: url('${import.meta.env.BASE_URL}assets/sprites/fruit-${f.name}.png');
        background-size: ${f.w * 4}px ${h}px;
        background-position: 0 0;
        background-repeat: no-repeat;
        transform: rotate(${f.rot}deg);
        transform-origin: center;
        filter: drop-shadow(0 4px 6px rgba(0,0,0,0.15));
      `;
      wrap.appendChild(el);
    }
    return wrap;
  }

  private openHowTo(): void {
    const body = document.createElement('div');
    body.style.textAlign = 'left';
    body.style.lineHeight = '1.5';
    body.innerHTML = `
      <p><strong>1.</strong> Drag the hand to aim, release to throw a fruit.</p>
      <p><strong>2.</strong> Two same fruits that touch will <em>merge</em> into the next size.</p>
      <p><strong>3.</strong> Don't let the bowl overflow past the red line!</p>
    `;
    const modal = createModal({
      title: 'HOW TO PLAY',
      body,
      actions: [
        createButton({
          variant: 'primary',
          label: 'GOT IT',
          onClick: () => modal.hide(),
        }),
      ],
      onDismiss: () => modal.destroy(),
    });
    document.body.appendChild(modal.el);
    modal.show();
  }

  private openSettings(): void {
    const state: Record<string, unknown> = {
      music: 60,
      sfx: 80,
      quality: 'mid',
      reduceMotion: false,
      colorBlind: false,
    };
    const panel = createSettingsPanel({
      title: 'SETTINGS',
      rows: [
        presetRows.music,
        presetRows.sfx,
        presetRows.quality,
        presetRows.reduceMotion,
        presetRows.colorBlind,
      ],
      values: state,
      onChange: (key, value) => {
        state[key] = value;
        this.registry.set(`setting-${key}`, value);
      },
      onClose: () => panel.destroy(),
    });
    document.body.appendChild(panel.el);
    panel.show();

    const moreGames = document.createElement('div');
    moreGames.style.cssText =
      'margin:20px 24px 0;padding-top:16px;border-top:1px solid rgba(0,0,0,0.08);';
    const heading = document.createElement('div');
    heading.textContent = 'MORE GAMES';
    heading.style.cssText = 'font:800 11px system-ui;letter-spacing:1.4px;color:rgba(90,42,31,0.55);margin-bottom:10px;';
    const link = document.createElement('a');
    link.href = '/fishmerge/';
    link.textContent = '🐠 Fish Game';
    link.style.cssText = 'display:block;padding:10px 14px;background:rgba(126,201,214,0.18);border-radius:10px;color:#1f3b4d;font:800 14px system-ui;text-decoration:none;';
    moreGames.appendChild(heading);
    moreGames.appendChild(link);
    panel.el.appendChild(moreGames);
  }
}
